import React, { useRef, useState } from "react";
import apiFetch from "../../utils/apiFetch";
import SmModal, { ConfirmModal } from "./SmModal";
import FieldForm from "./FieldForm";
import { MAINTENANCE_FIELDS } from "./equipmentConfig";

const DIRECTUS_URL = process.env.REACT_APP_DIRECTUS_URL || "http://127.0.0.1:8055";
const MAX_FILE_SIZE = 20 * 1024 * 1024;

export default function MaintenanceSection({
  substationId,
  equipmentType,
  equipmentId,
  maintTable,
  records = [],
  canCreate,
  canEdit,
  canDelete,
  onRefresh,
}) {
  const [modalMode, setModalMode] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [form, setForm] = useState({});
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const addFileRef = useRef(null);

  function openCreate() {
    setForm({});
    setFiles([]);
    setEditRecord(null);
    setModalMode("create");
  }

  function openEdit(rec) {
    setForm({
      topic: rec.topic || "",
      event_date: rec.event_date ? rec.event_date.split("T")[0] : "",
      comment: rec.comment || "",
      result: rec.result || "",
    });
    setEditRecord(rec);
    setFiles([]);
    setModalMode("edit");
  }

  function validateFiles(selected) {
    for (const f of selected) {
      if (f.size > MAX_FILE_SIZE) {
        alert(`"${f.name}" ҳажми ${(f.size / 1024 / 1024).toFixed(1)} МБ — максимум 20 МБ`);
        return false;
      }
    }
    return true;
  }

  function handleFileSelect(e) {
    const selected = Array.from(e.target.files || []);
    if (!validateFiles(selected)) return;
    setFiles((prev) => [...prev, ...selected]);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function handleSave() {
    if (!form.topic?.trim()) return alert("Мавзу киритилиши шарт");
    setSaving(true);
    try {
      if (modalMode === "create") {
        const fd = new FormData();
        fd.append("topic", form.topic);
        if (form.event_date) fd.append("event_date", form.event_date);
        if (form.comment) fd.append("comment", form.comment);
        if (form.result) fd.append("result", form.result);
        for (const f of files) fd.append("files", f);

        const res = await apiFetch(
          `/api/substation-management/${substationId}/equipment/${equipmentType}/${equipmentId}/maintenance`,
          { method: "POST", body: fd }
        );
        if (!res.ok) throw new Error((await res.json()).message);
      } else {
        const res = await apiFetch(
          `/api/substation-management/${substationId}/maintenance/${maintTable}/${editRecord.id}`,
          { method: "PATCH", body: form }
        );
        if (!res.ok) throw new Error((await res.json()).message);
      }
      setModalMode(null);
      onRefresh();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleAddFiles(recordId, e) {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    if (!validateFiles(selected)) return;
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of selected) fd.append("files", f);
      const res = await apiFetch(
        `/api/substation-management/${substationId}/maintenance/${maintTable}/${recordId}/files`,
        { method: "POST", body: fd }
      );
      if (!res.ok) throw new Error((await res.json()).message);
      onRefresh();
    } catch (e) {
      alert(e.message);
    } finally {
      setUploading(false);
      if (addFileRef.current) addFileRef.current.value = "";
    }
  }

  async function handleDeleteFile(recordId, fileId) {
    if (!window.confirm("Бу файлни ўчирмоқчимисиз?")) return;
    try {
      const res = await apiFetch(
        `/api/substation-management/${substationId}/maintenance/${maintTable}/${recordId}/files/${fileId}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error((await res.json()).message);
      onRefresh();
    } catch (e) {
      alert(e.message);
    }
  }

  async function handleDelete() {
    try {
      const res = await apiFetch(
        `/api/substation-management/${substationId}/maintenance/${maintTable}/${deleteTarget.id}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error((await res.json()).message);
      setDeleteTarget(null);
      onRefresh();
    } catch (e) {
      alert(e.message);
    }
  }

  function fmtDate(d) {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("uz-UZ");
  }

  function getFiles(record) {
    return record.files || [];
  }

  return (
    <div className="sm-maint sm-maint--detail">
      <div className="sm-maint__title">
        <span>Эксплуатация ва таъмир ({records.length})</span>
        {canCreate && (
          <button type="button" className="sm-btn sm-btn--primary sm-btn--sm" onClick={openCreate}>
            Ёзув
          </button>
        )}
      </div>

      {records.length === 0 && <div className="sm-detail-empty sm-detail-empty--compact">Ёзувлар топилмади</div>}

      {records.map((r) => (
        <article key={r.id} className="sm-equip-row sm-maint-row">
          <button type="button" className="sm-equip-row__head" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
            <span className="sm-equip-row__name">{r.topic || "—"}</span>
            <span className="sm-equip-row__meta">
              <span>{fmtDate(r.event_date)}</span>
              <span>{r.author_user ? `${r.author_user.name || ""} ${r.author_user.lastname || ""}`.trim() : ""}</span>
              {getFiles(r).length > 0 && <span>Файл: {getFiles(r).length}</span>}
            </span>
            <span className={`sm-equip-row__toggle ${expanded === r.id ? "is-open" : ""}`}>&#9660;</span>
          </button>

          {expanded === r.id && (
            <div className="sm-equip-row__body">
              <div className="sm-form sm-form--detail sm-form--compact">
                <div className="sm-field"><span className="sm-field__label">Мавзу</span><span className="sm-detail-field__value">{r.topic || "—"}</span></div>
                <div className="sm-field"><span className="sm-field__label">Сана</span><span className="sm-detail-field__value">{fmtDate(r.event_date)}</span></div>
                <div className="sm-field"><span className="sm-field__label">Натижа</span><span className="sm-detail-field__value">{r.result || "—"}</span></div>
                <div className="sm-field"><span className="sm-field__label">Изоҳ</span><span className="sm-detail-field__value">{r.comment || "—"}</span></div>
              </div>

              {getFiles(r).length > 0 && (
                <div className="sm-asset-files">
                  <span className="sm-asset-files__title">Файллар ({getFiles(r).length})</span>
                  <div className="sm-asset-files__grid">
                    {getFiles(r).map((f) => (
                      <div key={f.id} className="sm-scheme-card sm-scheme-card--file">
                        <img
                          className="sm-scheme-card__img"
                          src={`${DIRECTUS_URL}/assets/${f.directus_files_id}?fit=cover&width=240&height=160`}
                          alt=""
                          onError={(e) => {
                            e.target.src =
                              "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='80'><rect width='100%' height='100%' fill='%23f1f5f9'/><text x='50%' y='50%' text-anchor='middle' dy='.3em' fill='%2394a3b8' font-size='12'>File</text></svg>";
                          }}
                        />
                        {canDelete && (
                          <button type="button" className="sm-scheme-card__delete" onClick={() => handleDeleteFile(r.id, f.id)}>
                            &#10005;
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {canEdit && (
                <div className="sm-file-action">
                  <label className="sm-btn sm-btn--ghost sm-btn--sm">
                    Файл қўшиш
                    <input
                      ref={addFileRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      onChange={(e) => handleAddFiles(r.id, e)}
                    />
                  </label>
                  {uploading && <span>Юкланмоқда...</span>}
                </div>
              )}

              <div className="sm-equip-row__actions">
                {canEdit && <button type="button" className="sm-btn sm-btn--ghost sm-btn--sm" onClick={() => openEdit(r)}>Таҳрирлаш</button>}
                {canDelete && <button type="button" className="sm-btn sm-btn--danger sm-btn--sm" onClick={() => setDeleteTarget(r)}>Ўчириш</button>}
              </div>
            </div>
          )}
        </article>
      ))}

      {modalMode && (
        <SmModal
          title={modalMode === "create" ? "Янги ёзув" : "Ёзувни таҳрирлаш"}
          onClose={() => setModalMode(null)}
          wide
          footer={
            <>
              <button type="button" className="sm-btn sm-btn--ghost" onClick={() => setModalMode(null)}>Бекор</button>
              <button type="button" className="sm-btn sm-btn--primary" onClick={handleSave} disabled={saving}>
                {saving ? "Сақланмоқда..." : "Сақлаш"}
              </button>
            </>
          }
        >
          <FieldForm fields={MAINTENANCE_FIELDS} values={form} onChange={setForm} className="sm-form sm-form--detail" />

          {modalMode === "create" && (
            <div className="sm-file-picker">
              <label className="sm-field__label">Файллар</label>
              <label className="sm-btn sm-btn--ghost sm-btn--sm">
                Файл танлаш
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleFileSelect}
                />
              </label>
              {files.length > 0 && (
                <div className="sm-file-picker__list">
                  {files.map((f, i) => (
                    <div key={`${f.name}-${i}`} className="sm-file-picker__item">
                      <span>{f.name} ({(f.size / 1024).toFixed(0)} КБ)</span>
                      <button type="button" className="sm-btn sm-btn--danger sm-btn--icon" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}>
                        &#10005;
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </SmModal>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Ўчириш"
          text="Бу ёзувни ўчирмоқчимисиз?"
          subText="Маълумот аудит-логга сақланади."
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
